import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { authService, AuthUser, signInWithEmail, registerWithEmail } from '../services/authService';
import { clearTimedTextCache } from '../services/timedTextService';

interface UserDoc {
  isActive: boolean;
  [key: string]: unknown;
}

interface AuthContextType {
  user: AuthUser | null;
  userDoc: UserDoc | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isBanned: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const unsubscribeDocRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      // Cancel previous document listener
      unsubscribeDocRef.current?.();
      unsubscribeDocRef.current = null;

      if (firebaseUser) {
        const authUser: AuthUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        };
        setUser(authUser);

        // Start real-time listener on the user document
        unsubscribeDocRef.current = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          async (snap) => {
            if (!snap.exists()) {
              setUserDoc(null);
              setIsLoading(false);
              return;
            }
            const data = snap.data() as UserDoc;
            // Real-time ban detection
            if (data.isActive === false) {
              unsubscribeDocRef.current?.();
              unsubscribeDocRef.current = null;
              await authService.signOut();
              return;
            }
            setUserDoc(data);
            setIsLoading(false);
          },
          () => {
            setIsLoading(false);
          },
        );
      } else {
        clearTimedTextCache();
        setUser(null);
        setUserDoc(null);
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeDocRef.current?.();
      unsubscribeDocRef.current = null;
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setIsLoading(true);
    try {
      const authUser = await authService.signInWithGoogle();
      const snap = await getDoc(doc(db, 'users', authUser.uid));
      if (snap.exists() && (snap.data() as UserDoc).isActive === false) {
        await authService.signOut();
        throw new Error('Your account has been suspended. Please contact support.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signInWithEmailHandler = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await signInWithEmail(email, password);
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (snap.exists() && (snap.data() as UserDoc).isActive === false) {
          await authService.signOut();
          throw new Error('Your account has been suspended. Please contact support.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registerWithEmailHandler = useCallback(async (email: string, password: string) => {
    await registerWithEmail(email, password);
  }, []);

  const signOut = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.signOut();
      clearTimedTextCache();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getIdToken = useCallback(async () => {
    return authService.getIdToken();
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      userDoc,
      isLoading,
      isAuthenticated: !!user,
      isBanned: userDoc?.isActive === false,
      signInWithGoogle,
      signInWithEmail: signInWithEmailHandler,
      registerWithEmail: registerWithEmailHandler,
      signOut,
      getIdToken,
    }),
    [
      user,
      userDoc,
      isLoading,
      signInWithGoogle,
      signInWithEmailHandler,
      registerWithEmailHandler,
      signOut,
      getIdToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
